/**
 * CRM page API wrapper (DNKADM-0499; dink-api DNKAPI-0716 U5 — its delivery doc's
 * "U5 API contract" section is the source).
 *
 * Contract (Bearer-only `/v2/admin/**`, OWNER/ADMIN; errors are the v2 `{code, message,
 * status}` envelope, whose top-level `code` the client exposes as `details.code`, read by
 * `v2ErrorCode`):
 *  - `GET  /v2/admin/crm` → {@link CrmOverview}: the connection, the 11 fixed fields then one
 *    per tracked service and whether each exists in the CRM, the tracked services, the last
 *    run's status (`RUNNING`/`DONE`/`FAILED`) and the existing membership/credit mirror.
 *    There is no last-run id, so a page opened mid-run cannot resume that run's progress.
 *  - `POST /v2/admin/crm/fields/ensure` → 200 {@link CrmEnsureResult}: finds or creates every
 *    Dink field; each field carries an `outcome` and `unfiled` (a top-level `folder` is read
 *    when present, never relied on).
 *    **No write scope is NOT an HTTP error**: still 200, with `errorCode`
 *    {@link CRM_FIELDS_NO_SCOPE}. Refused `409 CRM_NOT_CONNECTED` / `502 CRM_UPSTREAM_ERROR`.
 *  - `POST /v2/admin/crm/sync` → 202 {@link CrmSyncRun} (a live run is returned as-is);
 *    refused `409 CRM_FIELDS_NOT_SET_UP` / `CRM_NOT_CONNECTED`.
 *  - `GET  /v2/admin/crm/sync/{runId}` → the run's counts and `status` (`RUNNING`, `DONE` —
 *    finished, `failed` may be above 0 — or `FAILED`, stopped early); `404
 *    CRM_SYNC_RUN_NOT_FOUND`.
 *  - `PUT  /v2/admin/services/{id}` takes `crmTracked` (in `./services`); a 4th tracked
 *    service is refused `409` {@link CRM_TRACK_LIMIT}.
 *
 * Every read is parsed DEFENSIVELY: a missing or odd-typed value degrades instead of
 * throwing. Codes and status words are DATA — the page maps them to plain sentences
 * ({@link crmErrorMessage}, {@link runResultWords}) and never shows one.
 */
import { request } from './client';
import { v2ErrorCode } from './v2Errors';
import { actionErrorMessage } from './screenError';

const PATH = '/v2/admin/crm';

/** The CRM connection can't create fields (missing write scope) — a 200 ensure `errorCode`. */
export const CRM_FIELDS_NO_SCOPE = 'CRM_FIELDS_NO_SCOPE';
/** A 4th service turned on for CRM tracking (409). */
export const CRM_TRACK_LIMIT = 'CRM_TRACK_LIMIT';
const CRM_NOT_CONNECTED = 'CRM_NOT_CONNECTED';
const CRM_UPSTREAM_ERROR = 'CRM_UPSTREAM_ERROR';
const CRM_FIELDS_NOT_SET_UP = 'CRM_FIELDS_NOT_SET_UP';
const CRM_SYNC_RUN_NOT_FOUND = 'CRM_SYNC_RUN_NOT_FOUND';
/** How many services a tenant may track in the CRM. */
export const CRM_TRACK_MAX = 3;

export const CRM_FIELDS_NO_SCOPE_MESSAGE =
  'Your CRM connection can’t create fields. Ask Journix support.';
export const CRM_TRACK_LIMIT_MESSAGE =
  'Up to 3 services can be tracked in your CRM. Turn one off on the CRM page first.';

/** Plain copy for each CRM code (never shown as the code itself). */
const CODE_MESSAGES: Record<string, string> = {
  [CRM_FIELDS_NO_SCOPE]: CRM_FIELDS_NO_SCOPE_MESSAGE,
  [CRM_TRACK_LIMIT]: CRM_TRACK_LIMIT_MESSAGE,
  [CRM_NOT_CONNECTED]: 'Your CRM isn’t connected. Ask Journix support.',
  [CRM_UPSTREAM_ERROR]: 'Your CRM didn’t respond. Please try again in a few minutes.',
  [CRM_FIELDS_NOT_SET_UP]: 'Set up the fields first, then fill in history. Use “Set up fields” above.',
  [CRM_SYNC_RUN_NOT_FOUND]: 'That update couldn’t be found. Try filling in history again.',
};

export interface CrmConnection {
  connected: boolean;
  /** The CRM account (location) name, or null when the CRM did not answer. */
  locationName: string | null;
}

export interface CrmField {
  code: string;
  /** The field's name in the CRM, e.g. "Dink Has Purchased". */
  name: string;
  ghlFieldKey: string | null;
  /** Whether the field exists in the CRM. */
  exists: boolean;
}

export interface CrmTrackedService {
  serviceId: string;
  name: string;
  /** e.g. "Dink Last Swing Evaluation". */
  fieldName: string;
}

export interface CrmSyncStatus {
  lastRunAt: string | null;
  /** The last run's status word, upper-cased (`RUNNING`/`DONE`/`FAILED`), or null. */
  lastRunResult: string | null;
  contactsInSync: number;
  contactsFailing: number;
  /** A plain-words sample of the last failure (no ids), or null. */
  lastErrorSample: string | null;
}

export interface CrmExistingMirror {
  membershipFields: boolean;
  creditBucketsMapped: number;
}

export interface CrmOverview {
  connection: CrmConnection;
  fields: CrmField[];
  trackedServices: CrmTrackedService[];
  sync: CrmSyncStatus;
  existingMirror: CrmExistingMirror;
}

export interface CrmEnsureResult {
  /** The 200's own `errorCode` (e.g. {@link CRM_FIELDS_NO_SCOPE}), or null on success. */
  errorCode: string | null;
  /** True when fields were created outside the "Dink" folder this call. */
  unfiled: boolean;
  /** How many fields the CRM refused (`outcome: FAILED`). */
  failedCount: number;
}

export interface CrmSyncRun {
  runId: string;
  total: number;
  written: number;
  unchanged: number;
  failed: number;
  /** The run's status word, upper-cased (`RUNNING`/`DONE`/`FAILED`), or null. */
  status: string | null;
}

// --- Defensive parsing helpers ---------------------------------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** A string or a finite number as an id string, else null. */
function asId(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return asText(value);
}

/** A status word, upper-cased so every comparison is case-safe; null when absent. */
function asStatus(value: unknown): string | null {
  const text = asText(value);
  return text === null ? null : text.toUpperCase();
}

/** A non-negative count (number or numeric string); anything else is 0. */
function asCount(value: unknown): number {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function parseField(raw: unknown): CrmField | null {
  if (!isObject(raw)) return null;
  const name = asText(raw['name']);
  if (name === null) return null;
  return {
    code: asText(raw['code']) ?? '',
    name,
    ghlFieldKey: asText(raw['ghlFieldKey']),
    exists: raw['exists'] === true,
  };
}

function parseTracked(raw: unknown): CrmTrackedService | null {
  if (!isObject(raw)) return null;
  const serviceId = asId(raw['serviceId']);
  const name = asText(raw['name']);
  if (serviceId === null || name === null) return null;
  return { serviceId, name, fieldName: asText(raw['fieldName']) ?? `Dink Last ${name}` };
}

function listOf<T>(raw: unknown, parse: (r: unknown) => T | null): T[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parse).filter((x): x is T => x !== null);
}

export function parseCrmOverview(body: unknown): CrmOverview {
  const o = isObject(body) ? body : {};
  const connection = isObject(o['connection']) ? o['connection'] : {};
  const sync = isObject(o['sync']) ? o['sync'] : {};
  const mirror = isObject(o['existingMirror']) ? o['existingMirror'] : {};
  return {
    connection: {
      connected: connection['connected'] === true,
      locationName: asText(connection['locationName']),
    },
    fields: listOf(o['fields'], parseField),
    trackedServices: listOf(o['trackedServices'], parseTracked),
    sync: {
      lastRunAt: asText(sync['lastRunAt']),
      lastRunResult: asStatus(sync['lastRunResult']),
      contactsInSync: asCount(sync['contactsInSync']),
      contactsFailing: asCount(sync['contactsFailing']),
      lastErrorSample: asText(sync['lastErrorSample']),
    },
    existingMirror: {
      membershipFields: mirror['membershipFields'] === true,
      creditBucketsMapped: asCount(mirror['creditBucketsMapped']),
    },
  };
}

/**
 * The ensure 200. "Unfiled" is any field with `unfiled: true` (the contract), or — only if
 * the server also sends a top-level `folder` — `folder.applied === false` when this call
 * created a field (a folder that was not applied with nothing created means nothing landed
 * unfiled).
 */
export function parseEnsure(body: unknown): CrmEnsureResult {
  const o = isObject(body) ? body : {};
  const fields = Array.isArray(o['fields']) ? o['fields'].filter(isObject) : [];
  const folder = isObject(o['folder']) ? o['folder'] : {};
  const created = fields.some((f) => asStatus(f['outcome']) === 'CREATED');
  return {
    errorCode: asText(o['errorCode']),
    unfiled: fields.some((f) => f['unfiled'] === true) || (folder['applied'] === false && created),
    failedCount: fields.filter((f) => asStatus(f['outcome']) === 'FAILED').length,
  };
}

export function parseRun(body: unknown, fallbackRunId = ''): CrmSyncRun {
  const o = isObject(body) ? body : {};
  return {
    runId: asId(o['runId']) ?? asId(o['id']) ?? fallbackRunId,
    total: asCount(o['total']),
    written: asCount(o['written']),
    unchanged: asCount(o['unchanged']),
    failed: asCount(o['failed']),
    status: asStatus(o['status']),
  };
}

/**
 * Whether a sync run has finished: any status word but `RUNNING` (case-safe) is terminal —
 * `DONE`, `FAILED`, and (tolerantly) any word this client does not know.
 * With no status word, done once `written + unchanged + failed` reaches a non-zero `total`.
 */
export function isSyncRunDone(run: CrmSyncRun): boolean {
  if (run.status !== null) return run.status !== 'RUNNING';
  return run.total > 0 && run.written + run.unchanged + run.failed >= run.total;
}

/** Whether a finished run stopped early (`FAILED`) rather than finishing. */
export function runStopped(status: string | null): boolean {
  return status === 'FAILED';
}

/**
 * A run status word in plain words, never the enum: `RUNNING`, `DONE` (finished — the counts
 * say how many failed) or `FAILED` (stopped early). An unknown word reads null (the caller
 * shows nothing).
 */
export function runResultWords(status: string | null): string | null {
  switch (status) {
    case 'RUNNING':
      return 'Updating now';
    case 'DONE':
      return 'Finished';
    case 'FAILED':
      return 'Stopped before finishing';
    default:
      return null;
  }
}

// --- Endpoints --------------------------------------------------------------------

/** `GET /v2/admin/crm` → the CRM page's overview. */
export async function getCrmOverview(): Promise<CrmOverview> {
  return parseCrmOverview(await request<unknown>('GET', PATH));
}

/** `POST /v2/admin/crm/fields/ensure` → find or create every Dink field. */
export async function ensureCrmFields(): Promise<CrmEnsureResult> {
  return parseEnsure(await request<unknown>('POST', `${PATH}/fields/ensure`));
}

/** `POST /v2/admin/crm/sync` → start (or rejoin the live) sync for this tenant. */
export async function startCrmSync(): Promise<CrmSyncRun> {
  return parseRun(await request<unknown>('POST', `${PATH}/sync`));
}

/** `GET /v2/admin/crm/sync/{runId}` → the run's counts and status. */
export async function getCrmSyncRun(runId: string): Promise<CrmSyncRun> {
  const body = await request<unknown>('GET', `${PATH}/sync/${encodeURIComponent(runId)}`);
  return parseRun(body, runId);
}

/** Plain copy for a CRM code, or null when the code is not a CRM one. */
export function crmCodeMessage(code: string | null | undefined): string | null {
  return code ? (CODE_MESSAGES[code] ?? null) : null;
}

/**
 * A CRM write's error as one plain sentence: the CRM codes read fixed copy; anything else
 * reads the server's own (brand-scrubbed) message or `fallback`. Never a code.
 */
export function crmErrorMessage(error: unknown, fallback: string): string {
  const known = crmCodeMessage(v2ErrorCode(error));
  if (known !== null) return known;
  const message = actionErrorMessage(error, fallback);
  // A bare machine code is never shown: an upper-snake word reads the fallback instead.
  return /^[A-Z][A-Z0-9_]+$/.test(message.trim()) ? fallback : message;
}
